import { createSlice } from '@reduxjs/toolkit';
import {
  checkout,
  createNewFolio, createPaymentSession,
  getFolioInvoicePreview,
  getFoliosOfReservation,
  getHotelInformation,
  getHotelRooms,
  getInvoicesByReservationId,
  getMultipleFolios,
  getPaymentMethods,
  getReservation,
  getRoomInformation,
  initiateSession,
  makePaymentByAccount,
  makePaymentByAuthorization,
  resetAll,
  setCurrentFolioIds,
  setCurrentTip,
} from './actions';

const initialState = {
  sessionId: null,
  hotelInformation: {
    color: null,
    logo: null,
  },
  roomInformation: null,
  allRooms: [],
  reservation: {
    begin: false,
    success: false,
    data: null,
    errorMessage: null,
  },
  guest: {
    data: null,
  },
  reservationFolios: {
    list: null,
  },
  currentFolioIds: {
    data: null,
  },
  selectedItems: [],
  currentTip: {
    data: null,
  },
  folios: {
    list: [],
    begin: false,
    success: false,
    error: false,
    errorMessage: null,
  },
  invoices: {
    previewList: [],
    list: [],
  },
  paymentSession: null,
  paymentMethod: {
    list: [],
  },
  createFolio: {
    begin: false,
    success: false,
    error: false,
    folioId: null,
    errorMessage: null,
  },
  moveCharges: {
    begin: false,
    success: false,
    error: false,
    data: null,
    errorMessage: null,
  },
  payment: {
    begin: false,
    success: false,
    response: null,
    error: false,
    errorMessage: null,
  },
  checkout: {
    begin: false,
    success: false,
    response: null,
    error: false,
    errorMessage: null,
  },
  urls: {
    genericStartPageUrl: '',
    startPageUrl: '',
    directoryPageUrl: '',
    yourBillPageUrl: '',
    tippingPageUrl: '',
    billingAddressPageUrl: '',
    paymentPageUrl: '',
    invoicesPageUrl: '',
    checkoutSuccessPageUrl: '',
    thankYouPageUrl: '',
  },
};

const slice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    reset: (state) => ({
      ...initialState,
      hotelInformation: state.hotelInformation,
      urls: state.urls,
    }),
    toggleItem: (state, action) => {
      const itemId = action.payload.id;
      const index = state.selectedItems.indexOf(itemId);
      if (index > -1) {
        state.selectedItems.splice(index, 1);
      } else {
        state.selectedItems.push(itemId);
      }
    },
    setUrls: (state, action) => {
      const { uniqueId, propertyId, selectedItemId } = action.payload;
      const rootUrl = `/hotel/${uniqueId}/property/${propertyId}`;
      const roomUrl = `${rootUrl}/${selectedItemId}`;
      state.urls = {
        genericStartPageUrl: `${rootUrl}/start`,
        startPageUrl: `${roomUrl}/start`,
        directoryPageUrl: `${roomUrl}/directory`,
        yourBillPageUrl: `${roomUrl}/your-bill`,
        tippingPageUrl: `${roomUrl}/tipping`,
        billingAddressPageUrl: `${roomUrl}/billing-address`,
        paymentPageUrl: `${roomUrl}/payment`,
        invoicesPageUrl: `${roomUrl}/invoices`,
        checkoutSuccessPageUrl: `${roomUrl}/checkout-success`,
        thankYouPageUrl: `${roomUrl}/thank-you`,
      };
    },
    clearSelectedItems: (state) => {
      state.selectedItems = initialState.selectedItems;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initiateSession.fulfilled, (state, action) => {
        if (action.payload) {
          state.sessionId = action.payload.sessionId;
          sessionStorage.setItem('sessionId', action.payload.sessionId);
        }
      })
      .addCase(getHotelInformation.fulfilled, (state, action) => {
        if (action.payload) {
          state.hotelInformation = { ...state.hotelInformation, ...action.payload };
        }
      })
      .addCase(getHotelRooms.fulfilled, (state, action) => {
        if (action.payload) {
          state.allRooms = action.payload;
        }
      })
      .addCase(getReservation.fulfilled, (state, action) => {
        if (action.payload && action.payload.length > 0) {
          state.reservation.data = action.payload.filter(
            (reservation) => reservation.status === 'InHouse',
          )[0];
        }
      })
      .addCase(getRoomInformation.fulfilled, (state, action) => {
        if (action.payload && action.payload) {
          state.roomInformation = action.payload;
        }
      })
      .addCase(getFoliosOfReservation.fulfilled, (state, action) => {
        state.reservationFolios = action.payload;
      })
      .addCase(getMultipleFolios.fulfilled, (state, action) => {
        state.folios.list = action.payload;
      })
      .addCase(getPaymentMethods.fulfilled, (state, action) => {
        state.paymentMethod.list = action.payload;
      })
      .addCase(createPaymentSession.fulfilled, (state, action) => {
        state.paymentSession = action.payload;
      })
      .addCase(makePaymentByAccount.pending, (state) => {
        state.payment.begin = true;
      })
      .addCase(makePaymentByAccount.fulfilled, (state, action) => {
        state.payment.begin = false;
        state.payment.success = true;
        state.payment.response = action.payload;
      })
      .addCase(makePaymentByAccount.rejected, (state, action) => {
        state.payment.begin = false;
        state.payment.success = false;
        state.payment.error = true;
        state.payment.errorMessage = action.payload;
      })
      .addCase(makePaymentByAuthorization.pending, (state) => {
        state.payment.begin = true;
      })
      .addCase(makePaymentByAuthorization.fulfilled, (state, action) => {
        state.payment.begin = false;
        state.payment.success = true;
        state.payment.response = action.payload;
      })
      .addCase(makePaymentByAuthorization.rejected, (state, action) => {
        state.payment.begin = false;
        state.payment.success = false;
        state.payment.error = true;
        state.payment.errorMessage = action.payload;
      })
      .addCase(getFolioInvoicePreview.fulfilled, (state, action) => {
        state.payment.success = false;
        state.invoices.previewList = action.payload;
      })
      .addCase(setCurrentFolioIds.fulfilled, (state, action) => {
        state.currentFolioIds.data = action.payload;
      })
      .addCase(setCurrentTip.fulfilled, (state, action) => {
        state.currentTip.data = action.payload;
      })
      .addCase(createNewFolio.pending, (state) => {
        state.createFolio.begin = true;
      })
      .addCase(createNewFolio.fulfilled, (state, action) => {
        state.createFolio.begin = false;
        state.createFolio.success = true;
        state.createFolio.folioId = action.payload;
      })
      .addCase(createNewFolio.rejected, (state, action) => {
        state.createFolio.begin = false;
        state.createFolio.success = false;
        state.createFolio.error = true;
        state.createFolio.errorMessage = action.payload;
      })
      .addCase(checkout.pending, (state) => {
        state.checkout.begin = true;
      })
      .addCase(checkout.fulfilled, (state, action) => {
        state.checkout.begin = false;
        state.checkout.success = true;
        state.checkout.response = action.payload;
      })
      .addCase(checkout.rejected, (state, action) => {
        state.checkout.begin = false;
        state.checkout.success = false;
        state.checkout.error = true;
        state.checkout.errorMessage = action.payload;
      })
      .addCase(getInvoicesByReservationId.fulfilled, (state, action) => {
        state.invoices.list = action.payload.invoices;
      })
      .addCase(resetAll.fulfilled, (state) => {
        Object.assign(state, initialState);
      });
  },
});

export const {
  reset, clearSelectedItems, toggleItem, setUrls,
} = slice.actions;

export default slice.reducer;
